import React from 'react';
import { motion } from 'framer-motion';

interface AlignmentTrackerProps {
    horizontalAlignments: number;
    verticalAlignments: number;
    diagonalAlignments: number;
    maxPossibleHorizontal?: number;
    maxPossibleVertical?: number;
    maxPossibleDiagonal?: number;
}

const AlignmentBar: React.FC<{
    label: string;
    count: number;
    max: number;
    icon: string;
    color: string;
}> = ({ label, count, max, icon, color }) => {
    const percentage = max > 0 ? (count / max) * 100 : 0;

    return (
        <div style={{ marginBottom: 'var(--space-md)' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-xs)',
                fontSize: '0.875rem',
                color: 'var(--text-secondary)'
            }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                    <span style={{ fontSize: '1rem' }}>{icon}</span>
                    <span>{label}</span>
                </span>
                <span style={{ fontWeight: '700', color: count > 0 ? color : 'var(--text-muted)' }}>
                    {count}/{max}
                </span>
            </div>

            <div style={{
                position: 'relative',
                height: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 'var(--radius-full)',
                overflow: 'hidden'
            }}>
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    style={{
                        height: '100%',
                        background: count > 0 ? color : 'transparent',
                        borderRadius: 'var(--radius-full)',
                        position: 'relative'
                    }}
                >
                    {count > 0 && (
                        <motion.div
                            animate={{
                                opacity: [0.5, 1, 0.5]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut'
                            }}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                            }}
                        />
                    )}
                </motion.div>

                {/* Segments indicators */}
                {max > 1 && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        pointerEvents: 'none'
                    }}>
                        {Array.from({ length: max - 1 }).map((_, i) => (
                            <div
                                key={i}
                                style={{
                                    flex: 1,
                                    borderRight: '1px solid rgba(255, 255, 255, 0.1)'
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const AlignmentTracker: React.FC<AlignmentTrackerProps> = ({
    horizontalAlignments,
    verticalAlignments,
    diagonalAlignments,
    maxPossibleHorizontal = 10,
    maxPossibleVertical = 4,
    maxPossibleDiagonal = 4
}) => {
    const totalAlignments = horizontalAlignments + verticalAlignments + diagonalAlignments;
    const bonusMultiplier = 1 + (totalAlignments * 0.5);

    return (
        <div className="glass-card" style={{ padding: 'var(--panel-padding)' }}>
            <div style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: 'var(--space-md)',
                color: 'var(--text-primary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>ALIGNEMENTS</span>
                {totalAlignments > 0 && (
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500 }}
                        style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            padding: '4px 12px',
                            background: 'var(--success)',
                            color: 'white',
                            borderRadius: 'var(--radius-full)'
                        }}
                    >
                        ×{bonusMultiplier.toFixed(1)}
                    </motion.span>
                )}
            </div>

            <AlignmentBar
                label="Horizontaux"
                count={horizontalAlignments}
                max={maxPossibleHorizontal}
                icon="━"
                color="linear-gradient(90deg, #3B82F6, #60A5FA)"
            />

            <AlignmentBar
                label="Verticaux"
                count={verticalAlignments}
                max={maxPossibleVertical}
                icon="┃"
                color="linear-gradient(90deg, #10B981, #34D399)"
            />

            <AlignmentBar
                label="Diagonaux"
                count={diagonalAlignments}
                max={maxPossibleDiagonal}
                icon="╱"
                color="linear-gradient(90deg, #F59E0B, #FBBF24)"
            />

            {/* Bonus explanation */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                    marginTop: 'var(--space-lg)',
                    padding: 'var(--space-md)',
                    background: totalAlignments > 0
                        ? 'rgba(16, 185, 129, 0.1)'
                        : 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 'var(--radius-md)',
                    border: `1px solid ${totalAlignments > 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.05)'}`
                }}
            >
                <div style={{
                    fontSize: '0.875rem',
                    color: totalAlignments > 0 ? 'var(--success)' : 'var(--text-muted)',
                    marginBottom: 'var(--space-xs)',
                    fontWeight: '600'
                }}>
                    💡 Bonus d'alignement
                </div>
                <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    lineHeight: '1.5'
                }}>
                    {totalAlignments > 0 ? (
                        <>
                            Vos <strong style={{ color: 'var(--success)' }}>{totalAlignments}</strong> alignement{totalAlignments > 1 ? 's' : ''}
                            {' '}multiplie{totalAlignments === 1 ? '' : 'nt'} votre score par{' '}
                            <strong style={{ color: 'var(--success)' }}>{bonusMultiplier.toFixed(1)}</strong>!
                        </>
                    ) : (
                        "Alignez 4 positions pour un bonus de +50% sur votre score total"
                    )}
                </div>
            </motion.div>

            {/* Achievement badges */}
            {totalAlignments >= 3 && (
                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.5 }}
                    style={{
                        marginTop: 'var(--space-md)',
                        padding: 'var(--space-md)',
                        background: 'linear-gradient(135deg, #FBBF24, #F59E0B)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'center',
                        color: '#78350F',
                        fontWeight: '700'
                    }}
                >
                    <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🏆</div>
                    <div style={{ fontSize: '0.875rem' }}>
                        Maître Stratège!
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default AlignmentTracker;
